/**
 * Miroir frontend de la source de vérité des catégories de disposition.
 * Backend : callizy_backend/telephony/disposition_categories.py — garder les
 * deux synchronisés.
 *
 * Une disposition = un nom + une CATÉGORIE. Le style du badge (couleur, icône,
 * libellé par défaut) et le comportement (finalité, éligibilité conversion)
 * dérivent de la catégorie. On ne stocke plus couleur/icône par disposition.
 */
import {
  Ban,
  Calendar,
  PhoneMissed,
  ThumbsDown,
  ThumbsUp,
  Voicemail,
  type LucideIcon,
} from 'lucide-react'
import type { DispositionCategory } from '@/types/powerDialer'

export interface CategoryStyle {
  /** Libellé humain de la catégorie (fallback si la disposition n'a pas de nom). */
  label: string
  /** Icône Lucide. */
  icon: LucideIcon
  /** Nom de couleur Tailwind (aligné sur le backend disposition_categories.py). */
  color: string
  /** L'appel n'est plus retenté (issue définitive). */
  isFinal: boolean
  /** Le prospect est éligible à la conversion en contact. */
  converts: boolean
  /** Classes Tailwind du badge, en thèmes sombre et clair. */
  badgeDark: string
  badgeLight: string
  /** Pastille de couleur (sélecteur de catégorie). */
  dot: string
}

export const CATEGORY_STYLES: Record<DispositionCategory, CategoryStyle> = {
  positive: {
    label: 'Positif',
    icon: ThumbsUp,
    color: 'green',
    isFinal: true,
    converts: true,
    badgeDark: 'bg-green-500/10 text-green-400 border-green-500/30',
    badgeLight: 'bg-green-50 text-green-600 border-green-200',
    dot: 'bg-green-500',
  },
  negative: {
    label: 'Négatif',
    icon: ThumbsDown,
    color: 'red',
    isFinal: true,
    converts: false,
    badgeDark: 'bg-red-500/10 text-red-400 border-red-500/30',
    badgeLight: 'bg-red-50 text-red-600 border-red-200',
    dot: 'bg-red-500',
  },
  callback: {
    label: 'Rappel',
    icon: Calendar,
    color: 'amber',
    isFinal: false,
    converts: false,
    badgeDark: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    badgeLight: 'bg-amber-50 text-amber-600 border-amber-200',
    dot: 'bg-amber-500',
  },
  neutral: {
    label: 'Neutre',
    icon: PhoneMissed,
    color: 'slate',
    isFinal: false,
    converts: false,
    badgeDark: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
    badgeLight: 'bg-slate-50 text-slate-500 border-slate-200',
    dot: 'bg-slate-400',
  },
  voicemail: {
    label: 'Messagerie',
    icon: Voicemail,
    color: 'purple',
    isFinal: false,
    converts: false,
    badgeDark: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
    badgeLight: 'bg-purple-50 text-purple-600 border-purple-200',
    dot: 'bg-purple-500',
  },
  disqualified: {
    label: 'Disqualifié',
    icon: Ban,
    color: 'red',
    isFinal: true,
    converts: false,
    badgeDark: 'bg-red-500/10 text-red-400 border-red-500/30',
    badgeLight: 'bg-red-50 text-red-600 border-red-200',
    dot: 'bg-red-500',
  },
}

const DEFAULT_CATEGORY: DispositionCategory = 'neutral'

export function styleForCategory(
  category?: DispositionCategory | null,
): CategoryStyle {
  if (category && category in CATEGORY_STYLES) return CATEGORY_STYLES[category]
  return CATEGORY_STYLES[DEFAULT_CATEGORY]
}

/** True si la catégorie impose une date (rappel). */
export function categoryRequiresDate(
  category?: DispositionCategory | null,
): boolean {
  return category === 'callback'
}

/** Options ordonnées pour un sélecteur de catégorie (création/édition). */
export const CATEGORY_OPTIONS: {
  value: DispositionCategory
  label: string
}[] = [
  { value: 'positive', label: 'Positif : intéressé, vente, RDV' },
  { value: 'callback', label: 'Rappel : à recontacter (date requise)' },
  { value: 'neutral', label: 'Neutre : pas de réponse' },
  { value: 'voicemail', label: 'Messagerie : boîte vocale' },
  { value: 'negative', label: 'Négatif : pas intéressé' },
  { value: 'disqualified', label: 'Disqualifié : faux numéro, hors cible' },
]
